import { useEffect, useState } from "react";
import api, { formatAxiosError } from "@/lib/api";
import { useDialog } from "@/context/DialogContext";
import { X, Plus, Tag, Trash, PencilSimple, Check } from "@phosphor-icons/react";

const PRESET_COLORS = ["#DC2626", "#EA580C", "#D97706", "#65A30D", "#059669", "#0891B2", "#0EA5E9", "#2563EB", "#7C3AED", "#DB2777", "#6B7280", "#0A0A0A"];

/**
 * Project Labels management modal.
 * Admin/manager/owner can add/edit/delete labels which then show up
 * on the task label picker + Kanban card pills.
 */
export default function ProjectLabelsModal({ projectId, canManage, onClose, onChanged }) {
  const { confirm } = useDialog();
  const [labels, setLabels] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [draft, setDraft] = useState({ name: "", color: PRESET_COLORS[7] });
  const [editing, setEditing] = useState(null); // { id, name, color }

  const load = async () => {
    try {
      const r = await api.get(`/projects/${projectId}/labels`);
      setLabels(r.data || []);
    } catch (e) { setMsg(formatAxiosError(e)); }
  };
  useEffect(() => { load(); }, [projectId]);

  const create = async () => {
    if (!draft.name.trim()) return;
    setBusy(true);
    try {
      await api.post(`/projects/${projectId}/labels`, draft);
      setDraft({ name: "", color: PRESET_COLORS[7] });
      await load();
      onChanged && onChanged();
    } catch (e) { setMsg(formatAxiosError(e)); }
    finally { setBusy(false); }
  };

  const update = async (lb) => {
    setBusy(true);
    try {
      await api.patch(`/projects/${projectId}/labels/${lb.id}`, { name: lb.name, color: lb.color });
      setEditing(null);
      await load();
      onChanged && onChanged();
    } catch (e) { setMsg(formatAxiosError(e)); }
    finally { setBusy(false); }
  };

  const remove = async (lb) => {
    const ok = await confirm({
      title: `Delete label "${lb.name}"?`,
      description: "The label will be removed from all tasks in this project.",
      confirmText: "Delete label",
      variant: "destructive",
    });
    if (!ok) return;
    setBusy(true);
    try {
      await api.delete(`/projects/${projectId}/labels/${lb.id}`);
      await load();
      onChanged && onChanged();
    } catch (e) { setMsg(formatAxiosError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="labels-modal">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white border border-border w-full max-w-md max-h-[85vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-border px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Tag size={16} weight="bold" />
            <h3 className="font-display text-lg font-bold">Project labels</h3>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-muted"><X size={16} weight="bold" /></button>
        </div>
        <div className="p-5 space-y-4">
          {msg && <div className="text-xs border border-red-500 bg-red-50 text-red-700 px-3 py-2">{msg}</div>}

          {labels.length === 0 && <div className="text-sm text-muted-foreground">No labels yet.</div>}

          <ul className="space-y-1">
            {labels.map(lb => (
              <li key={lb.id} className="flex items-center gap-2 border border-border px-3 py-2" data-testid={`label-row-${lb.id}`}>
                {editing?.id === lb.id ? (
                  <>
                    <input
                      value={editing.name}
                      onChange={e => setEditing({ ...editing, name: e.target.value })}
                      className="flex-1 border border-border px-2 py-1 text-sm"
                      autoFocus
                    />
                    <div className="flex gap-1">
                      {PRESET_COLORS.map(c => (
                        <button key={c} onClick={() => setEditing({ ...editing, color: c })} className={`w-4 h-4 border ${editing.color === c ? "border-black ring-1 ring-black" : "border-transparent"}`} style={{ backgroundColor: c }} />
                      ))}
                    </div>
                    <button onClick={() => update(editing)} disabled={busy} className="p-1 hover:bg-green-100 text-green-700"><Check size={14} weight="bold" /></button>
                    <button onClick={() => setEditing(null)} className="p-1 hover:bg-muted"><X size={14} /></button>
                  </>
                ) : (
                  <>
                    <span className="inline-block w-3 h-3" style={{ backgroundColor: lb.color }} />
                    <span className="flex-1 text-sm font-semibold">{lb.name}</span>
                    {canManage && (
                      <>
                        <button onClick={() => setEditing({ id: lb.id, name: lb.name, color: lb.color })} className="p-1 hover:bg-muted"><PencilSimple size={14} /></button>
                        <button onClick={() => remove(lb)} className="p-1 hover:bg-red-100 text-red-700"><Trash size={14} /></button>
                      </>
                    )}
                  </>
                )}
              </li>
            ))}
          </ul>

          {canManage && (
            <div className="border-t border-border pt-4 space-y-2">
              <div className="eyebrow text-[10px]">Add a label</div>
              <div className="flex gap-2">
                <input
                  value={draft.name}
                  onChange={e => setDraft({ ...draft, name: e.target.value })}
                  onKeyDown={e => e.key === "Enter" && create()}
                  placeholder="e.g. backend"
                  className="flex-1 border border-border px-3 py-2 text-sm"
                  data-testid="label-new-name"
                />
                <button onClick={create} disabled={busy || !draft.name.trim()} className="btn-primary text-xs px-3 py-2 flex items-center gap-1" data-testid="label-new-save">
                  <Plus size={12} weight="bold" /> Add
                </button>
              </div>
              <div className="flex flex-wrap gap-1">
                {PRESET_COLORS.map(c => (
                  <button
                    key={c}
                    onClick={() => setDraft({ ...draft, color: c })}
                    className={`w-6 h-6 border ${draft.color === c ? "border-black ring-1 ring-black" : "border-transparent"}`}
                    style={{ backgroundColor: c }}
                    aria-label={`color ${c}`}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Multi-select label picker used on task create / detail.
 */
export function LabelPicker({ projectId, value = [], onChange, label = "Labels" }) {
  const [labels, setLabels] = useState([]);
  useEffect(() => {
    api.get(`/projects/${projectId}/labels`).then(r => setLabels(r.data || [])).catch(() => {});
  }, [projectId]);
  if (!labels.length) return null;
  const toggle = (id) => {
    onChange(value.includes(id) ? value.filter(x => x !== id) : [...value, id]);
  };
  return (
    <div>
      <label className="eyebrow block mb-2">{label}</label>
      <div className="flex flex-wrap gap-1.5">
        {labels.map(lb => {
          const on = value.includes(lb.id);
          return (
            <button
              type="button"
              key={lb.id}
              onClick={() => toggle(lb.id)}
              className={`inline-flex items-center gap-1 text-xs px-2 py-1 border ${on ? "text-white" : "bg-white hover:bg-muted"}`}
              style={on ? { backgroundColor: lb.color, borderColor: lb.color } : { borderColor: lb.color, color: lb.color }}
              data-testid={`label-toggle-${lb.id}`}
            >
              <span className="inline-block w-2 h-2" style={{ backgroundColor: on ? "#fff" : lb.color }} />
              {lb.name}
            </button>
          );
        })}
      </div>
    </div>
  );
}
